import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Layers,
  Cable,
  Shield,
  Radio,
  Fingerprint,
  PhoneCall,
  Webhook,
  Globe,
  ChevronRight,
  Wand2,
  RefreshCw,
  FileCode,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui/page-header";
import { Section } from "@/components/ui/section";
import { DescriptionList } from "@/components/ui/description-list";
import { StatTile } from "@/components/ui/stat-tile";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useServerStatus } from "@/hooks/use-server-status";
import { useActiveCalls, useCallHistoryCount } from "@/hooks/use-calls";
import { useCurrentMinute } from "@/hooks/use-current-minute";
import { useEndpoints } from "@/hooks/use-endpoints";
import { useContexts } from "@/hooks/use-contexts";
import { useTransports } from "@/hooks/use-transports";
import { useAuths } from "@/hooks/use-auths";
import { useAors } from "@/hooks/use-aors";
import { useIdentifies } from "@/hooks/use-identifies";
import { useExtensions } from "@/hooks/use-extensions";
import { useAriApps } from "@/hooks/use-ari-apps";
import { useWebsocketEndpoints } from "@/hooks/use-websocket-endpoints";
import { useGenerateConfig, useReloadConfig } from "@/hooks/use-config";
import { channelLabel, summarizeActiveCalls } from "@/lib/calls";

function count(data: unknown): number | null {
  return Array.isArray(data) ? data.length : null;
}

export default function OverviewPage() {
  const { data: status } = useServerStatus();
  const activeCalls = useActiveCalls();
  // The 24h / 7d windows used to be computed once, at mount, so a tab left open
  // all day kept asking for "the last 24 hours" as of whenever it was opened.
  // useCurrentMinute re-derives them on a timer without moving the value on
  // every render — see the hook for why both extremes break react-query.
  const now = useCurrentMinute();
  const dates = useMemo(
    () => ({
      dayAgo: new Date(now - 24 * 3600 * 1000).toISOString(),
      weekAgo: new Date(now - 7 * 24 * 3600 * 1000).toISOString(),
    }),
    [now]
  );
  const calls24h = useCallHistoryCount({ date_from: dates.dayAgo });
  const calls7d = useCallHistoryCount({ date_from: dates.weekAgo });

  const endpoints = useEndpoints();
  const contexts = useContexts();
  const transports = useTransports();
  const auths = useAuths();
  const aors = useAors();
  const identifies = useIdentifies();
  const extensions = useExtensions();
  const ariApps = useAriApps();
  const wsEndpoints = useWebsocketEndpoints();

  const generate = useGenerateConfig();
  const reload = useReloadConfig();

  // Counting the rows of /api/calls/active counts *channels*, and one call is
  // several channels — this tile said "2 active calls" while Active Calls said
  // "1" for the same single call. Both pages now go through the same helper.
  const callStats = useMemo(
    () =>
      summarizeActiveCalls(Array.isArray(activeCalls.data) ? activeCalls.data : []),
    [activeCalls.data]
  );

  // States first, then the channel count — so a call showing as several
  // channels reads as an explanation rather than a contradiction.
  const callBreakdown = [
    callStats.up > 0 ? `${callStats.up} up` : null,
    callStats.ringing > 0 ? `${callStats.ringing} ringing` : null,
    callStats.channelCount > 0 ? channelLabel(callStats.channelCount) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const inventory = [
    { label: "Trunk Contexts", path: "/config/contexts", icon: <Layers size={16} />, query: contexts },
    { label: "Transports", path: "/config/transports", icon: <Cable size={16} />, query: transports },
    { label: "Auths", path: "/config/auths", icon: <Shield size={16} />, query: auths },
    { label: "AORs", path: "/config/aors", icon: <Radio size={16} />, query: aors },
    { label: "Identifies", path: "/config/identifies", icon: <Fingerprint size={16} />, query: identifies },
    { label: "Extensions", path: "/config/extensions", icon: <PhoneCall size={16} />, query: extensions },
    { label: "ARI Apps", path: "/config/ari-apps", icon: <Webhook size={16} />, query: ariApps },
    { label: "WebSocket", path: "/config/websocket-endpoints", icon: <Globe size={16} />, query: wsEndpoints },
  ];

  const handleGenerate = () => {
    generate.mutate("all", {
      onSuccess: (data) =>
        toast.success(`Config generated: ${Object.keys(data.results).join(", ")}`),
      onError: () => toast.error("Config generation failed"),
    });
  };

  const handleReload = () => {
    reload.mutate("all", {
      onSuccess: () => toast.success("Config reloaded"),
      onError: () => toast.error("Config reload failed"),
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Overview"
        description="Live traffic, configured resources, and server state at a glance."
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleGenerate}
              loading={generate.isPending}
              title="Write the current database configuration to Asterisk config files"
            >
              <Wand2 size={14} />
              Generate Config
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleReload}
              loading={reload.isPending}
              title="Reload Asterisk modules to apply generated config"
            >
              <RefreshCw size={14} />
              Reload
            </Button>
          </>
        }
      />

      <Section title="Server">
        <DescriptionList
          items={[
            {
              label: "Public IP",
              value: status?.public_ip ?? "-",
              mono: true,
              copyable: status?.public_ip ?? undefined,
              info: "Public IP address of this server, used as the SIP contact host.",
            },
            {
              label: "SIP URL",
              value: status?.public_url?.replace(/^sip:/, "") ?? "-",
              mono: true,
              copyable: status?.public_url?.replace(/^sip:/, "") ?? undefined,
              info: "Public SIP domain registered for this server. DNS is updated by the ddns-init container before Asterisk starts.",
            },
          ]}
        />
      </Section>

      <Section title="Traffic">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label="Active calls"
            value={callStats.callCount}
            sub={callBreakdown || "no calls in progress"}
            info="Calls in progress on the PBX, including those still ringing. One call uses several channels — dialling out through a trunk is a Local channel pair plus the outbound leg — so the channel count below is usually higher. Refreshes every 5 seconds."
            tone={callStats.callCount > 0 ? "success" : "default"}
            loading={activeCalls.isLoading}
            className="bg-background"
          />
          <StatTile
            label="Calls (24h)"
            value={calls24h.data?.count ?? "-"}
            info="Calls recorded in the last 24 hours, counted per call rather than per channel."
            loading={calls24h.isLoading}
            className="bg-background"
          />
          <StatTile
            label="Calls (7 days)"
            value={calls7d.data?.count ?? "-"}
            info="Calls recorded in the last 7 days, counted per call rather than per channel. History is retained for 7 days."
            loading={calls7d.isLoading}
            className="bg-background"
          />
          <StatTile
            label="Endpoints"
            value={count(endpoints.data) ?? "-"}
            sub={
              count(contexts.data) != null
                ? `across ${count(contexts.data)} context${count(contexts.data) === 1 ? "" : "s"}`
                : undefined
            }
            info="PJSIP endpoints configured across all trunk contexts."
            loading={endpoints.isLoading}
            className="bg-background"
          />
        </div>
      </Section>

      <Section
        title="Resources"
        action={
          <Link
            to="/config/view"
            className="inline-flex items-center gap-1.5 text-[13px] text-primary hover:underline"
          >
            <FileCode size={14} />
            View generated config
          </Link>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 stagger-children">
          {inventory.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="group block rounded-sm border border-border bg-background px-5 py-4 transition-[border-color,box-shadow] duration-150 ease-out cursor-pointer hover:border-border-hover hover:shadow-medium focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30"
            >
              <div className="flex h-4 items-center gap-2 text-muted-foreground">
                <span className="transition-colors duration-150 group-hover:text-foreground">
                  {item.icon}
                </span>
                <span className="text-[13px] font-medium leading-none text-foreground">
                  {item.label}
                </span>
                <div className="flex-1" />
                <ChevronRight
                  size={14}
                  className="transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-foreground"
                />
              </div>
              {item.query.isLoading ? (
                <Skeleton className="mt-2 h-7 w-16" />
              ) : (
                <div className="mt-2 text-2xl font-semibold tracking-tight tabular-nums text-foreground">
                  {count(item.query.data) ?? "-"}
                </div>
              )}
              <div className="mt-1 text-xs text-muted-foreground">configured</div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}
